import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../../Components/Views/ViewItem";
import { MailHtmlFrame, renderSignaturePreview, sanitizeEmailHtml } from "../../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../../Controllers/Controllers";
import { STATUS_LABELS, LeadCategoryType, LeadType, LeadsPagination } from "../../../../DataTypes/leads";
import { ComposeEmailFields } from "../../../../DataTypes/mail";
import { EmailTemplatePreviewResult, EmailTemplateType } from "../../../../DataTypes/templates";
import { RECIPIENT_MODE_OPTIONS, RecipientMode, getDefaultRecipientMode, getRecipientOptionLabel, getResolvedRecipients } from "../../../../Helpers/LeadRecipients";
import { SourceFilter, SourceSelectView } from "../../../UIComponents/SourceSelectView";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import '../Mail/mail-styles.css'
import '../../Settings/settings-styles.css'
import '../TemplateForm/template-form-styles.css'
import './compose-styles.css'

const PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 350

const SOURCE_FILTER_VALUES: SourceFilter[] = ['all', 'outreach', 'research']

const parseFiltersFromUrl = () => {
    const params = new URLSearchParams(window.location.search)
    const page = Number(params.get('page'))
    const status = params.get('status') || 'all'
    const source = params.get('source') || 'all'
    return {
        page: Number.isInteger(page) && page > 0 ? page : 1,
        status: status in STATUS_LABELS ? status : 'all',
        search: params.get('search') || '',
        source: (SOURCE_FILTER_VALUES as string[]).includes(source) ? source as SourceFilter : 'all',
        categoryId: params.get('category') || 'all',
    }
}

const getRecipientEmail = (lead: LeadType) => lead.decision_maker_email || lead.company_email || ''

const emptyComposeFields: ComposeEmailFields = {
    to: '',
    cc: '',
    subject: '',
    body_text: '',
    include_signature: true,
}

type AccountSignatureFields = {
    display_name: string;
    position: string;
    phone: string;
    email_address: string;
}

// A custom signature is only a sending problem if it's actually missing
// the unsubscribe token — checked from the backend preview's own
// placeholders_used list (the same regex-based detection the backend
// uses for real sends) rather than re-parsing raw HTML on the client.
// Only meaningful once a template (and therefore a real preview) is
// selected — the no-template fallback signature isn't covered by this,
// same limitation as before.
const customSignatureMissingUnsubscribe = (preview: EmailTemplatePreviewResult | null): boolean => {
    if (!preview?.signature) return false
    if (preview.signature.source !== 'custom') return false
    return !preview.signature.placeholders_used.includes('unsubscribe_url')
}

type ComposePageProps = viewItemProps & {
    leadId?: string;
}

type ComposePageState = {
    leads: LeadType[];
    pagination: LeadsPagination | null;
    leadsLoading: boolean;
    leadsError: string | null;
    needsAuth: boolean;
    search: string;
    status: string;
    categories: LeadCategoryType[];
    categoryId: string;
    source: SourceFilter;
    page: number;

    selectedLead: LeadType | null;
    sentLeadIds: number[];
    justSent: boolean;
    recipientMode: RecipientMode;
    ccVisible: boolean;

    templates: EmailTemplateType[];
    selectedTemplateId: string;
    templateLoading: boolean;
    templateError: string | null;
    templatePreview: EmailTemplatePreviewResult | null;

    fields: ComposeEmailFields;
    sending: boolean;
    sendError: string | null;
    sendMessage: string | null;

    signatureTemplateHtml: string;
    accountFields: AccountSignatureFields | null;
    signatureLoading: boolean;
}

class ComposePage extends ViewItem {
    props: ComposePageProps;
    state: ComposePageState;
    searchTimer: any = null;

    constructor(props: ComposePageProps) {
        super(props)
        this.props = props
        const urlFilters = parseFiltersFromUrl()
        this.state = {
            leads: [],
            pagination: null,
            leadsLoading: true,
            leadsError: null,
            needsAuth: false,
            categories: [],
            ...urlFilters,

            selectedLead: null,
            sentLeadIds: [],
            justSent: false,
            recipientMode: 'decision_maker',
            ccVisible: false,

            templates: [],
            selectedTemplateId: '',
            templateLoading: false,
            templateError: null,
            templatePreview: null,

            fields: { ...emptyComposeFields },
            sending: false,
            sendError: null,
            sendMessage: null,

            signatureTemplateHtml: '',
            accountFields: null,
            signatureLoading: true,
        }
    }

    componentDidMount(): void {
        this.syncUrlWithState()
        this.loadLeads()
        this.loadCategories()
        this.loadSignatureContext()
        this.loadTemplates()
        if (this.props.leadId) this.loadLeadFromUrl()
    }

    syncUrlWithState = () => {
        const { page, status, search, source, categoryId } = this.state
        const params = new URLSearchParams()
        if (page > 1) params.set('page', String(page))
        if (status !== 'all') params.set('status', status)
        if (search) params.set('search', search)
        if (source !== 'all') params.set('source', source)
        if (categoryId !== 'all') params.set('category', categoryId)
        const qs = params.toString()
        const newUrl = window.location.pathname + (qs ? `?${qs}` : '')
        window.history.replaceState(null, '', newUrl)
    }

    applyFilters = () => {
        this.syncUrlWithState()
        this.loadLeads()
    }

    loadCategories = async () => {
        const response = await controllers().outreach.getCategories()
        if (response && response.statusCode === 200) {
            this.setState({ categories: response.data?.categories || [] })
        }
    }

    loadTemplates = async () => {
        if (!controllers().auth.userToken) return
        const response = await controllers().mail.getTemplates()
        if (response && response.statusCode === 200) {
            this.setState({ templates: response.data?.templates || [] })
        }
    }

    // Opens the lead named in the URL (/outreach/compose/:leadId) directly
    // into the compose panel, independent of whichever page the leads list
    // happens to be on — no need to search/paginate to find it first.
    loadLeadFromUrl = async () => {
        const leadId = Number(this.props.leadId)
        if (!leadId || !controllers().auth.userToken) return
        const response = await controllers().outreach.getLeadDetail(leadId)
        if (response && response.statusCode === 200 && response.data) {
            this.selectLead(response.data)
        }
    }

    componentWillUnmount(): void {
        if (this.searchTimer) clearTimeout(this.searchTimer)
    }

    loadLeads = async () => {
        const { search, page, status, source, categoryId } = this.state
        this.setState({ leadsLoading: true, leadsError: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ leadsLoading: false, needsAuth: true })
            return
        }
        const response = await controllers().outreach.getLeads({
            search: search || undefined,
            page,
            per_page: PER_PAGE,
            has_contact_email: true,
            is_from_research: source === 'all' ? undefined : source === 'research',
            status: status !== 'all' ? status : undefined,
            category_id: categoryId !== 'all' ? Number(categoryId) : undefined,
        })
        if (!response || response.statusCode !== 200) {
            this.setState({ leadsLoading: false, leadsError: response?.error || 'Failed to load leads.' })
            return
        }
        this.setState({
            leads: response.data?.leads || [],
            pagination: response.data?.pagination || null,
            leadsLoading: false,
        })
    }

    // Only used as the FALLBACK signature when no template is selected —
    // there's no backend endpoint to preview a signature outside of a
    // template context, so this stays a client-side render of the shared
    // signature + the current user's own account. The moment a template
    // is picked, the signature block switches to templatePreview.signature
    // instead (see renderComposePanel), which correctly reflects a
    // template-pinned sending_user or a per-template custom signature.
    loadSignatureContext = async () => {
        this.setState({ signatureLoading: true })
        if (!controllers().auth.userToken) {
            this.setState({ signatureLoading: false })
            return
        }
        const [signatureResponse, settingsResponse] = await Promise.all([
            controllers().mail.getSharedSignature(),
            controllers().mail.getSettings(),
        ])
        const account = settingsResponse?.data
        this.setState({
            signatureLoading: false,
            signatureTemplateHtml: (signatureResponse && signatureResponse.statusCode === 200) ? (signatureResponse.data?.html || '') : '',
            accountFields: (settingsResponse && settingsResponse.statusCode === 200 && account) ? {
                display_name: account.display_name || '',
                position: account.position || '',
                phone: account.phone || '',
                email_address: account.email_address || '',
            } : null,
        })
    }

    handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        this.setState({ search: value })
        if (this.searchTimer) clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
            this.setState({ page: 1 }, this.applyFilters)
        }, SEARCH_DEBOUNCE_MS)
    }

    handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ status: e.target.value, page: 1 }, this.applyFilters)
    }

    handleCategoryChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ categoryId: e.target.value, page: 1 }, this.applyFilters)
    }

    handleSourceChange = (value: SourceFilter) => {
        this.setState({ source: value, page: 1 }, this.applyFilters)
    }

    goToPage = (page: number) => {
        const { pagination } = this.state
        if (pagination && (page < 1 || page > pagination.total_pages)) return
        this.setState({ page }, this.applyFilters)
    }

    // Switching leads only updates the recipient — subject/body/cc carry
    // over on purpose, so you can draft one message and send it down the
    // list to several leads without retyping it each time. A selected
    // template is the one exception: its placeholders (company name,
    // decision maker, ...) are lead-specific, so it re-renders for the
    // newly selected lead rather than carrying over stale text.
    selectLead = (lead: LeadType) => {
        this.setState((prev: ComposePageState) => ({
            selectedLead: lead,
            recipientMode: getDefaultRecipientMode(lead),
            justSent: false,
            sendError: null,
            sendMessage: null,
            fields: {
                ...prev.fields,
                lead_id: lead.id,
            },
        }))
        if (this.state.selectedTemplateId) {
            this.loadTemplatePreview(Number(this.state.selectedTemplateId), lead.id)
        }
    }

    handleComposeAnother = () => {
        this.setState({ selectedLead: null, justSent: false, sendError: null, sendMessage: null })
    }

    handleTemplateChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const templateId = e.target.value
        this.setState({ selectedTemplateId: templateId, templateError: null, templatePreview: null })
        if (!templateId) {
            // Clearing template_id here matters just as much as clearing
            // body_html — without it, a Send right after deselecting would
            // still tell the backend "this came from template N" and
            // resolve the signature/permissions against a template that's
            // no longer actually selected in the UI.
            this.setState((prev: ComposePageState) => ({ fields: { ...prev.fields, body_html: undefined, template_id: undefined } }))
            return
        }
        this.loadTemplatePreview(Number(templateId), this.state.selectedLead?.id)
    }

    // Deliberately called with no third argument — unlike
    // ComposeCampaignPage, this page never passes for_campaign, so the
    // backend always resolves the signature identity as an individual
    // (the template's pinned sending_user if set, otherwise whoever's
    // composing) rather than the main account. See
    // mail_views.preview_email_template's for_campaign flag.
    loadTemplatePreview = async (templateId: number, leadId?: number) => {
        this.setState({ templateLoading: true, templateError: null })
        const response = await controllers().mail.previewTemplate(templateId, leadId)
        if (!response || response.statusCode !== 200) {
            this.setState({ templateLoading: false, templateError: response?.error || 'Failed to load template preview.' })
            return
        }
        const preview: EmailTemplatePreviewResult = response.data
        this.setState((prev: ComposePageState) => ({
            templateLoading: false,
            templatePreview: preview,
            fields: {
                ...prev.fields,
                subject: preview.subject,
                body_text: preview.body_text,
                body_html: preview.body_html || undefined,
                // This is the field that actually matters for the fix:
                // subject/body_text/body_html above were ALREADY reaching
                // send_email correctly (they're sent as literal text), but
                // template_id itself was never being stored anywhere, so
                // the backend had no way to know a template was used at
                // all — it silently fell back to SharedEmailSignature +
                // whoever's logged in, regardless of this template's own
                // custom_signature_html or pinned sending_user. Sending
                // template_id lets send_email resolve the signature
                // exactly the way this preview already does.
                template_id: templateId,
            },
        }))
    }

    handleRecipientModeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ recipientMode: e.target.value as RecipientMode, sendError: null, sendMessage: null })
    }

    setField = (field: keyof ComposeEmailFields, value: string | boolean) => {
        this.setState((prev: ComposePageState) => ({
            fields: { ...prev.fields, [field]: value },
            sendError: null,
            sendMessage: null,
        }))
    }

    handleTextChange = (field: keyof ComposeEmailFields) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setField(field, e.target.value)
    }

    // 'both_separate' resolves to two addresses, meaning two independent
    // sendEmail() calls (each recipient only ever sees their own address) —
    // every other mode resolves to exactly one, whether that's a single
    // address or a comma-joined "both" one.
    handleSend = async () => {
        const { fields, selectedLead, recipientMode, templatePreview } = this.state
        if (!selectedLead) return

        if (fields.include_signature && customSignatureMissingUnsubscribe(templatePreview)) {
            this.setState({
                sendError: "This template's custom signature doesn't include an unsubscribe link — fix it on the template before sending.",
            })
            return
        }

        const recipients = getResolvedRecipients(selectedLead, recipientMode)
        if (!recipients.length) {
            this.setState({ sendError: 'This lead has no email address for the selected option.' })
            return
        }

        this.setState({ sending: true, sendError: null, sendMessage: null })
        const results = await Promise.all(recipients.map(to => controllers().mail.sendEmail({ ...fields, to })))
        const failures = results.filter(r => !r || r.statusCode !== 200)

        if (failures.length === results.length) {
            this.setState({ sending: false, sendError: failures[0]?.error || 'Failed to send email.' })
            return
        }

        const sentCount = results.length - failures.length
        this.setState((prev: ComposePageState) => ({
            sending: false,
            justSent: true,
            sendMessage: failures.length
                ? `Sent ${sentCount} of ${results.length} emails — some failed.`
                : recipients.length > 1
                    ? `Sent ${sentCount} emails to ${selectedLead.company_name}.`
                    : `Sent to ${selectedLead.company_name}.`,
            sentLeadIds: !prev.sentLeadIds.includes(selectedLead.id)
                ? [...prev.sentLeadIds, selectedLead.id]
                : prev.sentLeadIds,
        }))

        // The backend flips the lead's status automatically once a send goes
        // through, so patch just this lead into the list rather than
        // reloading the whole page — keeps search/scroll position intact.
        const refreshed = await controllers().outreach.getLeadDetail(selectedLead.id)
        if (refreshed && refreshed.statusCode === 200 && refreshed.data) {
            this.setState((prev: ComposePageState) => ({
                leads: prev.leads.map(l => l.id === refreshed.data.id ? refreshed.data : l),
                selectedLead: prev.selectedLead?.id === refreshed.data.id ? refreshed.data : prev.selectedLead,
            }))
        }
    }

    renderPagination = () => {
        const { pagination, page } = this.state
        if (!pagination || pagination.total_pages <= 1) return null
        return (
            <div className="outreach-pagination compose-pagination">
                <span className="outreach-pagination-info">
                    Page {pagination.current_page} of {pagination.total_pages}
                </span>
                <div className="outreach-pagination-controls">
                    <button className="outreach-pagination-btn" disabled={!pagination.has_previous} onClick={() => this.goToPage(page - 1)}>
                        Previous
                    </button>
                    <button className="outreach-pagination-btn" disabled={!pagination.has_next} onClick={() => this.goToPage(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        )
    }

    renderLeadsList = () => {
        const { leads, selectedLead, sentLeadIds } = this.state
        return (
            <div className="mail-list">
                {leads.map(lead => {
                    const recipient = getRecipientEmail(lead)
                    return (
                        <div
                            key={lead.id}
                            className={`mail-list-item ${selectedLead?.id === lead.id ? 'mail-list-item-active' : ''}`}
                            onClick={() => this.selectLead(lead)}
                        >
                            <div className="mail-list-item-top">
                                <span className="mail-list-item-sender">
                                    {sentLeadIds.includes(lead.id) && <span className="compose-sent-check" title="Already sent this session">✓</span>}
                                    {lead.company_name}
                                </span>
                                <span className={`compose-status-chip lead-status-${lead.status}`}>
                                    {STATUS_LABELS[lead.status] || lead.status}
                                </span>
                            </div>
                            <div className="mail-list-item-subject">
                                {lead.decision_maker_name || 'No contact name'}
                            </div>
                            <div className={`mail-list-item-snippet ${!recipient ? 'compose-no-email' : ''}`}>
                                {recipient || 'No email on file'}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    renderComposePanel = () => {
        const { selectedLead, justSent, fields, sending, sendError, sendMessage, sentLeadIds, signatureTemplateHtml, accountFields, signatureLoading, recipientMode, ccVisible, templates, selectedTemplateId, templateLoading, templateError, templatePreview } = this.state

        if (!selectedLead) {
            return (
                <div className="mail-thread-panel mail-thread-empty">
                    <span className="outreach-muted">Select a lead on the left to start a new email.</span>
                </div>
            )
        }

        if (justSent) {
            return (
                <div className="mail-thread-panel compose-panel compose-success-panel">
                    <div className="compose-success-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </div>
                    <h2 className="compose-success-title">Email sent</h2>
                    <p className="compose-success-text">{sendMessage}</p>
                    <div className="compose-success-actions">
                        <button className="outreach-add-btn" onClick={this.handleComposeAnother}>
                            Compose Another Email
                        </button>
                    </div>
                </div>
            )
        }

        const signatureBlocked = fields.include_signature && customSignatureMissingUnsubscribe(templatePreview)

        return (
            <div className="mail-thread-panel compose-panel">
                <div className="mail-thread-header">
                    <div className="mail-thread-header-info">
                        <h2 className="mail-thread-subject">New Email</h2>
                        <p className="mail-thread-participants">to {selectedLead.company_name}</p>
                    </div>
                    <div className="mail-thread-header-actions">
                        <select
                            className="outreach-status-select compose-template-select"
                            value={selectedTemplateId}
                            onChange={this.handleTemplateChange}
                            disabled={sending}
                        >
                            <option value="">No template</option>
                            {templates.map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </select>
                        {sentLeadIds.includes(selectedLead.id) && (
                            <span className="compose-sent-badge">
                                <span className="compose-sent-check">✓</span>
                                Sent
                            </span>
                        )}
                    </div>
                </div>

                {sendError && <div className="category-form-error">{sendError}</div>}
                {sendMessage && (
                    <div className="outreach-banner outreach-banner-info settings-email-success">
                        <span className="outreach-banner-text">{sendMessage}</span>
                    </div>
                )}

                {signatureBlocked && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">
                            This template's custom signature doesn't include an unsubscribe link — recipients would have no way to opt out. Fix it on the template before sending.
                        </span>
                    </div>
                )}

                <div className="category-form-group">
                    <div className="compose-to-label-row">
                        <label className="category-form-label">To</label>
                        {!ccVisible && (
                            <button
                                type="button"
                                className="compose-cc-toggle"
                                onClick={() => this.setState({ ccVisible: true })}
                                disabled={sending}
                            >
                                + Cc
                            </button>
                        )}
                    </div>
                    <select
                        className="category-form-input"
                        value={recipientMode}
                        onChange={this.handleRecipientModeChange}
                        disabled={sending}
                    >
                        {RECIPIENT_MODE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value} disabled={getResolvedRecipients(selectedLead, value).length === 0}>
                                {getRecipientOptionLabel(selectedLead, value, label)}
                            </option>
                        ))}
                    </select>
                </div>

                {ccVisible && (
                    <div className="category-form-group">
                        <div className="settings-signature-label-row">
                            <label className="category-form-label">Cc</label>
                            <button
                                type="button"
                                className="compose-cc-toggle"
                                onClick={() => this.setState({ ccVisible: false })}
                                disabled={sending}
                            >
                                Remove
                            </button>
                        </div>
                        <input
                            className="category-form-input"
                            value={fields.cc}
                            onChange={this.handleTextChange('cc')}
                            placeholder="Optional"
                            disabled={sending}
                        />
                    </div>
                )}

                <div className="category-form-group">
                    <label className="category-form-label">Subject</label>
                    <input
                        className="category-form-input"
                        value={fields.subject}
                        onChange={this.handleTextChange('subject')}
                        disabled={sending}
                    />
                </div>
                <div className="category-form-group">
                    <label className="category-form-label">Message</label>
                    <textarea
                        className="category-form-textarea"
                        value={fields.body_text}
                        onChange={this.handleTextChange('body_text')}
                        rows={8}
                        disabled={sending}
                    />
                </div>

                {selectedTemplateId && (
                    <div className="settings-signature-preview compose-signature-preview">
                        <span className="settings-signature-preview-label">Template Preview</span>
                        {templateLoading && (
                            <div className="outreach-state">
                                <div className="outreach-spinner" />
                            </div>
                        )}
                        {!templateLoading && templateError && (
                            <div className="outreach-banner outreach-banner-error">
                                <span className="outreach-banner-text">{templateError}</span>
                            </div>
                        )}
                        {!templateLoading && !templateError && templatePreview && (
                            <>
                                {templatePreview.unrecognized_placeholders.length > 0 && (
                                    <div className="outreach-banner outreach-banner-error">
                                        <span className="outreach-banner-text">
                                            Unrecognized placeholder{templatePreview.unrecognized_placeholders.length === 1 ? '' : 's'}: {templatePreview.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                        </span>
                                    </div>
                                )}
                                {fields.body_html ? (
                                    <MailHtmlFrame html={sanitizeEmailHtml(fields.body_html)} />
                                ) : (
                                    <p className="outreach-muted template-preview-empty">This template has no HTML body — the message above will be sent as plain text.</p>
                                )}
                            </>
                        )}
                    </div>
                )}

                <label className="category-form-checkbox-row">
                    <input
                        type="checkbox"
                        checked={fields.include_signature}
                        onChange={(e) => this.setField('include_signature', e.target.checked)}
                        disabled={sending}
                    />
                    Include signature
                </label>

                {fields.include_signature && (
                    <div className="settings-signature-preview compose-signature-preview">
                        <span className="settings-signature-preview-label">
                            Signature Preview
                            {selectedTemplateId && templatePreview?.signature && (
                                <> ({templatePreview.signature.source === 'custom' ? 'custom for this template' : 'shared'})</>
                            )}
                        </span>

                        {selectedTemplateId ? (
                            // A template is selected: the signature comes from
                            // the SAME preview response used for the body above
                            // (templatePreview.signature), so it automatically
                            // reflects a pinned sending_user, or this template's
                            // own custom_signature_html — exactly what a real
                            // send_email call with this template_id will use.
                            <>
                                {templateLoading && (
                                    <div className="outreach-state">
                                        <div className="outreach-spinner" />
                                    </div>
                                )}
                                {!templateLoading && templateError && (
                                    <div className="outreach-banner outreach-banner-error">
                                        <span className="outreach-banner-text">{templateError}</span>
                                    </div>
                                )}
                                {!templateLoading && !templateError && templatePreview?.signature && (
                                    <>
                                        {templatePreview.signature.unrecognized_placeholders.length > 0 && (
                                            <div className="outreach-banner outreach-banner-error">
                                                <span className="outreach-banner-text">
                                                    Unrecognized placeholder{templatePreview.signature.unrecognized_placeholders.length === 1 ? '' : 's'}: {templatePreview.signature.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                                </span>
                                            </div>
                                        )}
                                        {templatePreview.signature.rendered_html ? (
                                            <MailHtmlFrame html={sanitizeEmailHtml(templatePreview.signature.rendered_html)} />
                                        ) : (
                                            <p className="outreach-muted compose-no-signature">No signature configured.</p>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            // No template selected: falls back to the shared
                            // signature rendered with your own account details,
                            // fetched once on mount — there's no backend
                            // endpoint to preview a signature outside of a
                            // template context.
                            <>
                                {signatureLoading && (
                                    <div className="outreach-state">
                                        <div className="outreach-spinner" />
                                    </div>
                                )}
                                {!signatureLoading && signatureTemplateHtml && (
                                    <MailHtmlFrame html={sanitizeEmailHtml(renderSignaturePreview(signatureTemplateHtml, accountFields || {}))} />
                                )}
                                {!signatureLoading && !signatureTemplateHtml && (
                                    <p className="outreach-muted compose-no-signature">No signature template has been set up yet.</p>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="settings-email-actions compose-send-actions">
                    <button className="outreach-add-btn" onClick={this.handleSend} disabled={sending || signatureBlocked}>
                        {sending ? 'Sending...' : 'Send Email'}
                    </button>
                </div>
            </div>
        )
    }

    render(): ReactNode {
        const { leads, leadsLoading, leadsError, needsAuth, search, status, categories, categoryId } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Compose</h1>
                        <p className="outreach-subtitle">Send emails to lead</p>
                    </div>
                    <div className="outreach-toolbar">
                        <input
                            type="text"
                            className="outreach-search"
                            placeholder="Search leads..."
                            value={search}
                            onChange={this.handleSearchChange}
                        />
                        <select className="outreach-status-select" value={status} onChange={this.handleStatusChange}>
                            <option value="all">All statuses</option>
                            {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <select className="outreach-status-select" value={categoryId} onChange={this.handleCategoryChange}>
                            <option value="all">All categories</option>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <SourceSelectView
                            source={this.state.source}
                            onSourceChange={this.handleSourceChange}
                        />
                    </div>
                </div>

                {!leadsLoading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to compose emails.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!leadsLoading && leadsError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{leadsError}</span>
                        <button className="outreach-banner-action" onClick={this.loadLeads}>
                            Retry
                        </button>
                    </div>
                )}

                {leadsLoading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length === 0 && (
                    <div className="outreach-state outreach-empty">
                        No leads found.
                    </div>
                )}

                {!leadsLoading && !leadsError && !needsAuth && leads.length > 0 && (
                    <div className="mail-layout">
                        <div className="mail-list-column">
                            {this.renderLeadsList()}
                            {this.renderPagination()}
                        </div>
                        {this.renderComposePanel()}
                    </div>
                )}
            </div>
        )
    }
}

export { ComposePage }